import React from 'react';
import i18n from 'i18next';

import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { addModal } from 'core/notifications';

import { Button } from 'uikit/buttons';
import { Typo } from 'uikit/content';

import EnrollmentInfoModal from '../components/enrollmentInfoModal';

import getShortDateTimeString from './getShortDateTimeString';

const getIsStartedActivity = ({
  relationship_type: relationshipType,
  due_on: dueOn,
  is_dropped: isDropped,
}) => {
  return relationshipType === 'elected' && !dueOn && !isDropped;
};

export const getMainTextContent = (enrollmentMetadata) => {
  const {
    is_overdue: isOverdue,
    is_dropped: isDropped,
    is_complete: isComplete,
    completed_on: completedOn,
    relationship_type: relationshipType,
    due_on: dueOn,
    recommended_by_ai: recommendedByAi,
    enrolled_on: enrolledOn,
  } = enrollmentMetadata;

  const isStartedActivity = getIsStartedActivity(enrollmentMetadata);

  if (isOverdue) {
    return (
      <Typo variant="s02" weight="medium" color="negative" testId="enrollment-message-main-text">
        {i18n.t('overdue-required-by', { date: getShortDateTimeString(dueOn) })}
      </Typo>
    );
  }

  if (isDropped) {
    const mainText = completedOn
      ? i18n.t('dropped-on', { date: getShortDateTimeString(completedOn) })
      : i18n.t('dropped');

    return (
      <Typo variant="s02" weight="medium" color="negative" testId="enrollment-message-main-text">
        {mainText}
      </Typo>
    );
  }

  if (isComplete) {
    const mainText = completedOn
      ? i18n.t('completed-on', { date: getShortDateTimeString(completedOn) })
      : i18n.t('completed');

    return (
      <Typo variant="s02" weight="medium" color="positive" testId="enrollment-message-main-text">
        {mainText}
      </Typo>
    );
  }

  let mainText = '';

  if (relationshipType === 'required') {
    mainText = dueOn
      ? i18n.t('required-by', { date: getShortDateTimeString(dueOn) })
      : i18n.t('required');
  } else if (relationshipType === 'recommended') {
    mainText = dueOn
      ? i18n.t('recommended-by', { date: getShortDateTimeString(dueOn) })
      : i18n.t('recommended');
  } else if (recommendedByAi) {
    mainText = isStartedActivity
      ? i18n.t('started-on', { date: getShortDateTimeString(enrolledOn) })
      : i18n.t('recommended-by-intellum-ai');
  }

  return (
    <Typo variant="s02" weight="medium" color="brand" testId="enrollment-message-main-text">
      {mainText}
    </Typo>
  );
};

export const getMetaContent = (enrollmentMetadata) => {
  const {
    is_complete: isComplete,
    is_dropped: isDropped,
    is_overdue: isOverdue,
    expires_on: expiresOn,
    recommended_by_ai: recommendedByAi,
  } = enrollmentMetadata;

  const isStartedActivity = getIsStartedActivity(enrollmentMetadata);

  if (isComplete || isDropped || isOverdue) {
    return [];
  }

  const metaContent = [];

  if (expiresOn) {
    metaContent.push(
      <Typo variant="s01" testId="enrollment-message-meta-text" key="expires-on">
        {i18n.t('expires-on', { date: getShortDateTimeString(expiresOn) })}
      </Typo>,
    );
  }

  if (isStartedActivity && recommendedByAi) {
    metaContent.push(
      <Typo variant="s01" testId="enrollment-message-meta-text" key="recommended-by-intellum-ai">
        {i18n.t('recommended-by-intellum-ai')}
      </Typo>,
    );
  }

  return metaContent;
};

export const getEnrollmentInfoModal = (enrollmentMetadata, courseType = 'Course') => {
  const {
    created_by: createdBy,
    due_on: dueOn,
    enrolled_on: enrolledOn,
    relationship_type: relationshipType,
    relationship_reason: reason,
  } = enrollmentMetadata;

  const isRequired = relationshipType === 'required';

  const title = isRequired
    ? i18n.t('required-activity', { activity: courseType })
    : i18n.t('recommended-activity', { activity: courseType });

  const body = isRequired
    ? i18n.t('set-as-required-by', { name: createdBy, date: getShortDateTimeString(enrolledOn) })
    : i18n.t('set-as-recommended-by', {
        name: createdBy,
        date: getShortDateTimeString(enrolledOn),
      });

  return addModal({
    hasCloseButton: true,
    title,
    body,
    component: <EnrollmentInfoModal dueOn={dueOn} reason={reason} />,
    actions: [
      {
        action: 'OKAY',
        text: i18n.t('okay'),
        color: 'brand',
        testId: 'enrollment-message-info-okay-button',
      },
    ],
  });
};

export const getActions = (enrollmentMetadata, courseType) => {
  const {
    due_on: dueOn,
    is_complete: isComplete,
    recommended_by_ai: recommendedByAi,
    relationship_type: relationshipType,
    relationship_reason: relationshipReason,
  } = enrollmentMetadata;
  const actions = [];

  if (isComplete || (!dueOn && !relationshipReason)) return null;

  const shouldShowInfo = ['required', 'recommended'].includes(relationshipType) || recommendedByAi;

  if (shouldShowInfo) {
    actions.push(
      <Button
        key="enrollment-message-info-button"
        testId="enrollment-message-info-button"
        text="Info"
        size="xsmall"
        icon={<InfoOutlined />}
        variant="ghost"
        onClick={() => getEnrollmentInfoModal(enrollmentMetadata, courseType)}
      />,
    );
  }
  return actions;
};
